/**
 * Radar Admin client facade — single entry point the RadarConfigPage uses
 * for the 8 Admin ops behind /ivt/radar-config (topic-model §3.2).
 *
 * One industry per request; the page's industry picker drives it. Returns
 * the generated shapes verbatim — the Smithy types already mark fields the
 * Admin page reads (`title`, `displayOrder`, keywords[]) as `string | undefined`
 * (Smithy @required is wire-shape-only), so the page narrows at the render
 * site rather than this facade pretending they are populated.
 *
 * Soft delete only: a "Remove topic" / "Delete news source" lands as
 * SetIndustry*Status(INACTIVE) — the row stays in the DB so §2.7's
 * TOPIC_REMOVED direction stamping has a referent, and the ACTIVE-only
 * list read drops it from the next paint.
 */

import {
  getIvtClient,
  ListIndustryTopicsCommand,
  GetIndustryTopicCommand,
  UpsertIndustryTopicCommand,
  SetIndustryTopicStatusCommand,
  GetIndustryGeneralTopicCommand,
  UpsertIndustryGeneralTopicCommand,
  ListIndustryNewsSourcesCommand,
  UpsertIndustryNewsSourceCommand,
  SetIndustryNewsSourceStatusCommand,
  type RadarStandardTopic,
  type RadarGeneralTopic,
  type RadarNewsSource,
  type RadarStandardTopicDraft,
  type RadarGeneralTopicDraft,
  type RadarNewsSourceDraft,
} from '../../client/IvtHubClient';

/** Collapsed snapshot the Admin page renders — one call to assemble it. */
export interface RadarConfigSnapshot {
  industry: string;
  /** ACTIVE STANDARD_TOPIC rows, ordered by displayOrder (service already sorts). */
  topics: RadarStandardTopic[];
  /**
   * The industry's single GENERAL_TOPIC row. `null` if the industry's M1 seed
   * has not run yet (topic-model §6 v1 only seeds LS_HC); the Admin page shows
   * a "GENERAL_TOPIC not seeded" state in that case rather than letting the
   * STO edit a form that cannot be saved — Upsert would raise.
   */
  general: RadarGeneralTopic | null;
  /** ACTIVE NEWS_SOURCE rows. */
  newsSources: RadarNewsSource[];
}

/**
 * True when `e` is a Smithy ResourceNotFoundException. Match on `name` only —
 * same shape the rest of this package's error-name sniffing uses
 * (useAssetFlow / useDismissal / AssetDetailHeader / UseCaseDetailPage) — so
 * a transport-level 404 (CloudFront misroute, API Gateway not-found) that
 * does NOT carry the Smithy fault name stays a real error and is NOT
 * silently mapped to the "GENERAL_TOPIC 未初始化" UX. The service's
 * contract is the authoritative signal; the HTTP status alone is not.
 */
function isResourceNotFound(e: unknown): boolean {
  return (e as { name?: string } | null)?.name === 'ResourceNotFoundException';
}

export const radarConfigClient = {
  /**
   * One industry, three list reads fired in parallel. GetIndustryGeneralTopic
   * throws ResourceNotFoundException for an industry whose GENERAL_TOPIC seed
   * has not run (topic-model §6 v1 only seeds LS_HC); we catch THAT ONE 404
   * and map it to `general: null` so the Admin page renders the "未初始化"
   * warning alongside the still-usable topics and news sources. Every other
   * failure, including a 404 on the other two reads (the industry itself is
   * missing), still rejects the snapshot.
   */
  async getSnapshot(industry: string): Promise<RadarConfigSnapshot> {
    const client = getIvtClient();
    // settle rather than Promise.all: Promise.all's fail-fast behaviour would
    // reject on the GENERAL 404 before the other two had a chance to settle,
    // undoing the parallelism and losing the useful partial state.
    const [topicsResult, generalResult, sourcesResult] = await Promise.allSettled([
      client.send(new ListIndustryTopicsCommand({ industry })),
      client.send(new GetIndustryGeneralTopicCommand({ industry })),
      client.send(new ListIndustryNewsSourcesCommand({ industry })),
    ]);
    // Check rejections in call-order (topics, general, sources) so a non-404
    // generalResult rejection is not silently dropped when another read also
    // fails — e.g. topics ok, general 500, sources 403 surfaces the 500 (the
    // first-rejected one), matching Promise.all's spirit.
    if (topicsResult.status === 'rejected') throw topicsResult.reason;
    let general: RadarGeneralTopic | null = null;
    if (generalResult.status === 'fulfilled') {
      general = generalResult.value.generalTopic ?? null;
    } else if (!isResourceNotFound(generalResult.reason)) {
      throw generalResult.reason;
    }
    if (sourcesResult.status === 'rejected') throw sourcesResult.reason;
    return {
      industry,
      topics: topicsResult.value.topics ?? [],
      general,
      newsSources: sourcesResult.value.sources ?? [],
    };
  },

  async getTopic(industry: string, topicId: string): Promise<RadarStandardTopic | null> {
    const out = await getIvtClient().send(new GetIndustryTopicCommand({ industry, topicId }));
    return out.topic ?? null;
  },

  /** Create (no topicId) or update (topicId); both return the row id. */
  async upsertTopic(
    industry: string,
    draft: RadarStandardTopicDraft,
    topicId?: string,
  ): Promise<string> {
    const out = await getIvtClient().send(
      new UpsertIndustryTopicCommand({ industry, topicId, draft }),
    );
    if (!out.topicId) throw new Error('UpsertIndustryTopic returned no topicId');
    return out.topicId;
  },

  /** Soft delete — the row stays in the DB; §2.7 CANDIDATE directions get stamped. */
  async setTopicStatus(
    industry: string,
    topicId: string,
    status: 'ACTIVE' | 'INACTIVE',
  ): Promise<void> {
    await getIvtClient().send(
      new SetIndustryTopicStatusCommand({ industry, topicId, status }),
    );
  },

  async upsertGeneral(industry: string, draft: RadarGeneralTopicDraft): Promise<void> {
    await getIvtClient().send(new UpsertIndustryGeneralTopicCommand({ industry, draft }));
  },

  async upsertNewsSource(
    industry: string,
    draft: RadarNewsSourceDraft,
    sourceId?: string,
  ): Promise<string> {
    const out = await getIvtClient().send(
      new UpsertIndustryNewsSourceCommand({ industry, sourceId, draft }),
    );
    if (!out.sourceId) throw new Error('UpsertIndustryNewsSource returned no sourceId');
    return out.sourceId;
  },

  async setNewsSourceStatus(
    industry: string,
    sourceId: string,
    status: 'ACTIVE' | 'INACTIVE',
  ): Promise<void> {
    await getIvtClient().send(
      new SetIndustryNewsSourceStatusCommand({ industry, sourceId, status }),
    );
  },
};
